const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/soh/eCheckin/sections/Versicherung/Versicherung.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import textMap from '../../translations';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import BehandlungsgrundField from 'App/components/eCheckin/components/fields/metakat/BehandlungsgrundField';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { MaternityBlock } from './Blocks/MaternityBlock';
import { AccidentBlock } from './Blocks/AccidentBlock';
import { createFieldPath, fieldValueIsNot } from 'App/components/eCheckin/utils';
import { Link, LinksBlock } from 'App/components/eCheckin/components/fields/LinksBlock';
import { Divider } from '@mui/material';
import { InsuranceDetailsBlock } from './Blocks/InsuranceDetails';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { css } from '@emotion/css';
import { useIntl } from 'react-intl';
import { InsuranceInformationField } from '../components/metakat/Versicherung/InsuranceInformation';
import { ZusatzLeistungenField } from '../components/metakat/Versicherung/ZusatzLeistungenField';
import { ZimmerBuchenField } from '../components/metakat/Versicherung/ZimmerBuchenField';

const broschureLinks: Link[] = [
    {
        name: 'burgerspitalSolothurn',
        text: textMap.burgerspitalSolothurn,
        href: 'https://www.solothurnerspitaeler.ch/fileadmin/Broschueren_Flyer/BSS/soH_bss_zusatzversicherte.pdf',
    },
    {
        name: 'kantonsspitalOlten',
        text: textMap.kantonsspitalOlten,
        href: 'https://www.solothurnerspitaeler.ch/fileadmin/Broschueren_Flyer/KSO/soH_kso_zusatzversicherte.pdf',
    },
    {
        name: 'Spitalclub',
        text: textMap.spitalClub,
        href: 'https://spitalclub.ch/',
    },
];

export const getInsurancePaths = (path: string) => ({
    PATH_BEHANDLUNGSGRUND: createFieldPath(path, 'reasonForTreatment'),
    PATH_AHV: createFieldPath(path, 'socialNumber'),
    PATH_INSURANCE_INFORMATION: createFieldPath(path, `insuranceInformation`),
    PATH_NS_NUMBER: createFieldPath(path, `nsNumber`),
    PATH_INSURANCE: createFieldPath(path, `insurance`),
    PATH_CARD_NUMBER: createFieldPath(path, `cardNumber`),
    PATH_INSURANCE_NUMBER: createFieldPath(path, `insuranceNumber`),
    PATH_INSURANCE_COVERAGE: createFieldPath(path, `insuranceCoverage`),
    PATH_INSURANCE_VVG: createFieldPath(path, `additionalInsuranceVVG`),
    PATH_ADDITIONAL_INSURANCE: createFieldPath(path, `additionalInsurance`),
    PATH_INSURANCE_NUMBER_ZV: createFieldPath(path, `insuranceNumberZV`),
    PATH_MORE_INFORMATION: createFieldPath(path, `moreInformation`),
    PATH_ADDITIONAL_INSURANCE_BENEFITS: createFieldPath(path, `additionalInsuranceBenefits`),
    PATH_ADDITIONAL_INSURANCE_SERVICES: createFieldPath(path, `additionalServicesInsurance`),
    PATH_MEDICAL_DATA: createFieldPath(path, `medicalData`),
    PATH_SINGLE_ROOM_BOOKING: createFieldPath(path, `singleRoomBooking`),
    PATH_MATERNITY_PACKET: createFieldPath(path, `maternityPacket`),
    PATH_WORKSTATUS: createFieldPath(path, `workStatus`),
    PATH_ACCIDENTDATE: createFieldPath(path, `accidentDate`),
    PATH_COMPANY: createFieldPath(path, `company`),
    PATH_EMPLOYER_REPORT: createFieldPath(path, `employerReport`),
    PATH_CLAIM_NR: createFieldPath(path, `claimNum`),
    PATH_ACCIDENT_INSURANCE: createFieldPath(path, `accidentInsurance`),
});

const moreInofrmationStyle = css`
    min-height: 100px;

    & .MuiInputBase-multiline,
    .MuiInputBase-inputMultiline {
        min-height: 100px;
    }

    & .MuiInputBase-multiline {
        padding: 0 !important;
    }
`;

export const Versicherung = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const formData = useCIS4mData();

    const { path } = props;
    const title = useText(props.title);
    const { formatMessage } = useIntl();

    const {
        PATH_BEHANDLUNGSGRUND,
        PATH_INSURANCE_INFORMATION,
        PATH_INSURANCE_COVERAGE,
        PATH_MORE_INFORMATION,
        PATH_ADDITIONAL_INSURANCE_BENEFITS,
        PATH_ADDITIONAL_INSURANCE_SERVICES,
        PATH_MEDICAL_DATA,
        PATH_SINGLE_ROOM_BOOKING,
    } = getInsurancePaths(path);

    // condition with PATH_INSURANCE_INFORMATION
    const hideSwissInsuranceFields = fieldValueIsNot(formData, PATH_INSURANCE_INFORMATION, [
        'auslandMitChVersicherung',
        'inlandMitChVersicherung',
        'militärversicherung',
    ]);

    // condition with PATH_INSURANCE_COVERAGE
    const hideSemiPrivateCoverage = fieldValueIsNot(formData, PATH_INSURANCE_COVERAGE, ['semi-private']);
    const hideAdditionalInsuranceBenefitsFields = fieldValueIsNot(formData, PATH_INSURANCE_COVERAGE, [
        'semi-private',
        'private',
    ]);

    const hideAdditionalInsuranceVVGFields = fieldValueIsNot(formData, PATH_INSURANCE_COVERAGE, [
        'general-Switzerland',
        'semi-private',
        'private',
    ]);

    const hideGeneralCoverage = fieldValueIsNot(formData, PATH_INSURANCE_COVERAGE, ['general-Switzerland', 'general']);

    const hideMaternityFields = fieldValueIsNot(formData, PATH_BEHANDLUNGSGRUND, ['maternity']);
    const hideAccidentFields = fieldValueIsNot(formData, PATH_BEHANDLUNGSGRUND, ['accident']);

    const eCheckinSectionRef = useCIS4m(ref);

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={props.title}>
            <h1>{title}</h1>
            <p>{formatMessage(textMap.insuranceInfoText)}</p>
            <BehandlungsgrundField label={textMap.reasonForTreatment} path={PATH_BEHANDLUNGSGRUND} required={true} />
            <AccidentBlock path={path} hidden={hideAccidentFields} />

            <InsuranceInformationField
                label={textMap.insuranceInformation}
                path={PATH_INSURANCE_INFORMATION}
                required={true}
            />

            <InsuranceDetailsBlock path={path} />

            <CIS4mTextField
                label={textMap.moreInformation}
                path={PATH_MORE_INFORMATION}
                className={moreInofrmationStyle}
                multiline={true}
                maxLength={250}
            />
            <YesNoField
                path={PATH_ADDITIONAL_INSURANCE_BENEFITS}
                label={textMap.additionalInsuranceBenefitsText}
                hidden={hideAdditionalInsuranceBenefitsFields}
                required={true}
            />
            <ZusatzLeistungenField
                label={textMap.additionalServicesInsurance}
                path={PATH_ADDITIONAL_INSURANCE_SERVICES}
                hidden={hideAdditionalInsuranceVVGFields && hideSwissInsuranceFields}
            />
            <YesNoField path={PATH_MEDICAL_DATA} label={textMap.medicalDataText} required={true} />

            <CIS4mBlock hidden={hideSwissInsuranceFields}>
                <ZimmerBuchenField
                    coverage={hideGeneralCoverage ? 'halbPrivat' : 'allgemein'}
                    label={textMap.singleRoomBooking}
                    path={PATH_SINGLE_ROOM_BOOKING}
                    hidden={hideSemiPrivateCoverage && hideGeneralCoverage}
                />
                <MaternityBlock path={path} hidden={hideMaternityFields} />
            </CIS4mBlock>
            <Divider />
            <LinksBlock links={broschureLinks} basePath={path} label={textMap.brosureLabel} />
        </ECheckinSection>
    );
});

Versicherung.displayName = 'Versicherung';

export default Versicherung;
